import { useState, useCallback, useEffect } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { TIMELINE, TIMELINE_IMAGES } from "@/lib/data";

export default function TimelineCarousel() {
  const [emblaRef, emblaApi] = useEmblaCarousel({ align: "start", containScroll: "trimSnaps", dragFree: false });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((idx) => emblaApi && emblaApi.scrollTo(idx), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
  }, [emblaApi]);

  return (
    <div className="relative" data-testid="timeline-carousel">
      {/* Year ruler */}
      <div className="hidden md:flex items-center gap-2 mb-10 overflow-x-auto pb-2">
        {TIMELINE.map((t, i) => (
          <button
            key={t.year}
            onClick={() => scrollTo(i)}
            data-testid={`timeline-year-btn-${t.year}`}
            className={`flex-shrink-0 px-3 py-1.5 text-xs font-bold tracking-wider transition-all border ${
              i === selectedIndex
                ? "border-slate-900 text-white"
                : "border-slate-300 text-slate-500 hover:border-slate-900 hover:text-slate-900"
            }`}
            style={i === selectedIndex ? { background: "var(--zt-navy)", borderColor: "var(--zt-navy)" } : {}}
          >
            {t.year}
          </button>
        ))}
      </div>

      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex">
          {TIMELINE.map((t, i) => (
            <div
              key={t.year}
              data-testid={`timeline-slide-${t.year}`}
              className="flex-shrink-0 min-w-0 mr-6 md:mr-8"
              style={{ flex: "0 0 90%", maxWidth: "90%" }}
            >
              <div className="grid md:grid-cols-2 gap-0 border border-slate-200 bg-white">
                <div
                  className="relative aspect-[4/3] md:aspect-auto md:min-h-[420px] overflow-hidden p-5 md:p-7"
                  style={{ background: "#F8FAFC" }}
                >
                  {/* Framed photo */}
                  <div
                    className="relative w-full h-full bg-white border border-slate-200"
                    style={{ boxShadow: "0 10px 30px rgba(15, 23, 42, 0.08), 0 2px 6px rgba(15, 23, 42, 0.04)" }}
                  >
                    <img
                      src={TIMELINE_IMAGES[t.image] || TIMELINE_IMAGES.modern}
                      alt={t.title}
                      className={`absolute inset-0 w-full h-full object-contain p-4 timeline-ken-burns ${i === selectedIndex ? "is-active" : ""}`}
                      loading="lazy"
                    />
                  </div>
                  {/* Year overlay (corner) */}
                  <div className="absolute bottom-4 left-4 right-4 pointer-events-none">
                    <p
                      className="text-6xl md:text-7xl font-bold leading-none"
                      style={{
                        color: "var(--zt-navy)",
                        fontFamily: "Outfit, sans-serif",
                        textShadow: "0 2px 12px rgba(255,255,255,0.95), 0 0 24px rgba(255,255,255,0.9)",
                      }}
                    >
                      {t.year}
                    </p>
                  </div>
                </div>
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  <h3 className="text-2xl md:text-3xl font-bold leading-tight mb-5">{t.title}</h3>
                  <p className="text-slate-600 leading-relaxed text-base md:text-lg">{t.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between mt-8">
        <div className="flex items-center gap-3">
          <button
            onClick={scrollPrev}
            data-testid="timeline-prev"
            className="w-12 h-12 flex items-center justify-center border border-slate-300 hover:border-slate-900 hover:bg-slate-900 hover:text-white transition-colors"
            aria-label="Previous"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={scrollNext}
            data-testid="timeline-next"
            className="w-12 h-12 flex items-center justify-center border border-slate-300 hover:border-slate-900 hover:bg-slate-900 hover:text-white transition-colors"
            aria-label="Next"
          >
            <ChevronRight size={20} />
          </button>
        </div>

        <div className="flex items-center gap-2">
          {scrollSnaps.map((snap, i) => (
            <button
              key={`snap-${snap}-${i}`}
              onClick={() => scrollTo(i)}
              data-testid={`timeline-dot-${i}`}
              aria-label={`Go to slide ${i + 1}`}
              className={`h-1 transition-all ${i === selectedIndex ? "w-10" : "w-4"}`}
              style={{ background: i === selectedIndex ? "var(--zt-navy)" : "#cbd5e1" }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
